import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { skillService } from '../../services/skillService';
import { goalService } from '../../services/goalService';
import { Skill, SkillFormData } from '../../types/Skill';
import { Goal, GoalCategory } from '../../types/Goal';
import PageHeader from '../common/PageHeader';
import './SkillMap.css';

interface SkillEditProps {
  onToggleSidebar: () => void;
}

const SkillEdit: React.FC<SkillEditProps> = ({ onToggleSidebar }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isEditing = id !== 'new' && id !== undefined;

  const [skill, setSkill] = useState<Skill | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>('');

  // Color options with labels
  const colorOptions = [
    { value: '', label: 'Default (White)', color: '#ffffff' },
    { value: '#f5f5f5', label: 'Light Grey', color: '#f5f5f5' },
    { value: '#f9f9f9', label: 'Very Light Grey', color: '#f9f9f9' },
    { value: '#fafafa', label: 'Off White', color: '#fafafa' },
    { value: '#e3f2fd', label: 'Light Blue', color: '#e3f2fd' },
    { value: '#bbdefb', label: 'Medium Blue', color: '#bbdefb' },
    { value: '#e8f5e9', label: 'Light Green', color: '#e8f5e9' },
    { value: '#c8e6c9', label: 'Medium Green', color: '#c8e6c9' },
    { value: '#fff9e6', label: 'Light Yellow', color: '#fff9e6' },
    { value: '#fff59d', label: 'Medium Yellow', color: '#fff59d' },
    { value: '#fce4ec', label: 'Light Pink', color: '#fce4ec' },
    { value: '#f8bbd0', label: 'Medium Pink', color: '#f8bbd0' },
    { value: '#f3e5f5', label: 'Light Purple', color: '#f3e5f5' },
    { value: '#e1bee7', label: 'Medium Purple', color: '#e1bee7' },
    { value: '#ffe0b2', label: 'Light Orange', color: '#ffe0b2' },
    { value: '#ffcc80', label: 'Medium Orange', color: '#ffcc80' },
    { value: '#e0f2f1', label: 'Light Teal', color: '#e0f2f1' },
    { value: '#b2dfdb', label: 'Medium Teal', color: '#b2dfdb' },
  ];

  // Goals state
  const [linkedGoals, setLinkedGoals] = useState<Goal[]>([]);
  const [availableGoals, setAvailableGoals] = useState<Goal[]>([]);
  const [showGoalSelector, setShowGoalSelector] = useState(false);
  const [selectedGoalId, setSelectedGoalId] = useState<string>('');
  const [showCreateGoalForm, setShowCreateGoalForm] = useState(false);
  const [creatingGoal, setCreatingGoal] = useState(false);
  
  // New goal form state
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [newGoalDescription, setNewGoalDescription] = useState('');
  const [newGoalCategory, setNewGoalCategory] = useState<GoalCategory>(GoalCategory.PERSONAL);
  const [newGoalStartedDate, setNewGoalStartedDate] = useState('');
  const [newGoalTargetDate, setNewGoalTargetDate] = useState('');

  // Menu state
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      if (isEditing && id) {
        loadSkill();
      } else {
        setLoading(false);
      }
      loadAvailableGoals();
    }
  }, [user, id, isEditing]);

  // Handle clicks outside menu to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  const loadSkill = async () => {
    if (!user || !id) return;

    setLoading(true);
    try {
      const response = await skillService.getSkillById(id);

      if (response.success && response.skill) {
        const skillData = response.skill;
        setSkill(skillData);
        setName(skillData.name);
        setDescription(skillData.description || '');
        setColor(skillData.properties?.color || '');
        // Convert linked goals from Firebase format to Goal format
        const convertedLinkedGoals = (skillData.linkedGoals || []).map((goal: any) => 
          goalService.convertFirebaseGoal(goal)
        );
        setLinkedGoals(convertedLinkedGoals);
      } else {
        setError('Skill not found');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load skill');
      console.error('Error loading skill:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableGoals = async () => {
    if (!user) return;

    try {
      const response = await goalService.getUserGoals({
        user_id: user.id.toString(),
        limit: 100,
        offset: 0
      });

      if (response.success && response.goals) {
        const convertedGoals = response.goals.map(goal => goalService.convertFirebaseGoal(goal));
        setAvailableGoals(convertedGoals);
      }
    } catch (err: any) {
      console.error('Error loading goals:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !name.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const skillData: SkillFormData = {
        name: name.trim(),
        description: description.trim() || undefined,
        properties: color ? { color } : undefined
      };

      if (isEditing && id) {
        // Update existing skill
        const result = await skillService.updateSkill({
          id: id,
          ...skillData
        });

        if (result.success) {
          setSuccessMessage('Skill updated successfully');
          setTimeout(() => {
            navigate('/skillmap');
          }, 1000);
        } else {
          throw new Error(result.message || 'Failed to update skill');
        }
      } else {
        // Create new skill
        const result = await skillService.createSkill(user.id.toString(), skillData);

        if (result.success && result.skill) {
          setSuccessMessage('Skill created successfully');
          // Navigate to edit page to allow goal linking
          setTimeout(() => {
            navigate(`/skillmap/${result.skill!.id}/edit`);
          }, 1000);
        } else {
          throw new Error(result.message || 'Failed to create skill');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save skill');
      console.error('Error saving skill:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleLinkGoal = async () => {
    if (!selectedGoalId || !id) return;

    try {
      const result = await skillService.linkGoalToSkill(id, parseInt(selectedGoalId));

      if (result.success) {
        // Reload skill to get updated linked goals
        if (isEditing) {
          await loadSkill();
        }
        setShowGoalSelector(false);
        setSelectedGoalId('');
      } else {
        setError(result.message || 'Failed to link goal');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to link goal');
      console.error('Error linking goal:', err);
    }
  };

  const handleUnlinkGoal = async (goalId: number) => {
    if (!id) return;

    if (window.confirm('Are you sure you want to unlink this goal?')) {
      try {
        const result = await skillService.unlinkGoalFromSkill(id, goalId);

        if (result.success) {
          // Reload skill to get updated linked goals
          if (isEditing) {
            await loadSkill();
          }
        } else {
          setError(result.message || 'Failed to unlink goal');
        }
      } catch (err: any) {
        setError(err.message || 'Failed to unlink goal');
        console.error('Error unlinking goal:', err);
      }
    }
  };

  const handleCreateGoal = async () => {
    if (!user || !newGoalTitle.trim()) {
      setError('Please enter a goal title');
      return;
    }

    setCreatingGoal(true);
    setError(null);

    try {
      const goalData = {
        title: newGoalTitle.trim(),
        description: newGoalDescription.trim() || '',
        category: newGoalCategory,
        startedDate: newGoalStartedDate || undefined,
        targetDate: newGoalTargetDate || undefined
      };

      const result = await goalService.createGoal(user.id.toString(), goalData);

      if (result.success && result.id) {
        // Reload available goals
        await loadAvailableGoals();
        
        // Automatically link the new goal to the skill if editing
        if (isEditing && id) {
          const linkResult = await skillService.linkGoalToSkill(id, result.id);
          if (linkResult.success) {
            await loadSkill();
          }
        }

        // Reset form
        setNewGoalTitle('');
        setNewGoalDescription('');
        setNewGoalCategory(GoalCategory.PERSONAL);
        setNewGoalStartedDate('');
        setNewGoalTargetDate('');
        setShowCreateGoalForm(false);
        setShowGoalSelector(false);
      } else {
        throw new Error(result.message || 'Failed to create goal');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create goal');
      console.error('Error creating goal:', err);
    } finally {
      setCreatingGoal(false);
    }
  };

  const getUnlinkedGoals = () => {
    const linkedGoalIds = linkedGoals.map(g => parseInt(g.id));
    return availableGoals.filter(g => !linkedGoalIds.includes(parseInt(g.id)));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return null;
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return null;
    }
  };

  const handleGoalClick = (goalId: string) => {
    navigate(`/goals/${goalId}`);
  };

  const leftActions = (
    <button
      className="skillmap-back-btn"
      onClick={() => navigate('/skillmap')}
      title="Back to Skill Roadmap"
    >
      ←
    </button>
  );

  const rightActions = (
    <div className="skillmap-edit-menu-container" ref={menuRef}>
      <button
        className="skillmap-edit-menu-btn"
        onClick={() => setShowMenu(!showMenu)}
        title="Menu"
      >
        ⋮
      </button>
      {showMenu && (
        <div className="skillmap-edit-menu-dropdown">
          <button
            className="skillmap-edit-menu-item"
            onClick={() => {
              navigate('/goals');
              setShowMenu(false);
            }}
          >
            Go to Goals
          </button>
          {isEditing && (
            <button
              className="skillmap-edit-menu-item"
              onClick={() => {
                setShowMenu(false);
                handleDelete();
              }}
              disabled={deleting || saving}
            >
              {deleting ? 'Deleting...' : 'Delete Skill'}
            </button>
          )}
        </div>
      )}
    </div>
  );

  const handleDelete = async () => {
    if (!id || !isEditing || !skill) return;

    if (!window.confirm(`Are you sure you want to delete "${skill.name}"? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const result = await skillService.deleteSkill(id);

      if (result.success) {
        setSuccessMessage('Skill deleted successfully');
        setTimeout(() => {
          navigate('/skillmap');
        }, 1000);
      } else {
        throw new Error(result.message || 'Failed to delete skill');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete skill');
      console.error('Error deleting skill:', err);
    } finally {
      setDeleting(false);
    }
  };

  if (!user) {
    return (
      <div className="skillmap-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="skillmap-header-title">{isEditing ? 'Edit Skill' : 'New Skill'}</h1>}
          leftActions={leftActions}
        />
        <div className="skillmap-auth-required">
          <p>Please sign in to manage your skills.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="skillmap-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="skillmap-header-title">{isEditing ? 'Edit Skill' : 'New Skill'}</h1>}
          leftActions={leftActions}
        />
        <div className="skillmap-loading">Loading skill...</div>
      </div>
    );
  }

  return (
    <div className="skillmap-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<h1 className="skillmap-header-title">{isEditing ? 'Edit Skill' : 'New Skill'}</h1>}
        leftActions={leftActions}
        rightActions={rightActions}
      />

      <div className="skillmap-content">
        {error && (
          <div className="skillmap-error-banner">
            <span>{error}</span>
            <button onClick={() => setError(null)}>×</button>
          </div>
        )}

        {successMessage && (
          <div className="skillmap-success-banner">
            <span>{successMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="skillmap-edit-form">
          <div className="skillmap-form-group">
            <label htmlFor="name">Name *</label>
            <input
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., hindi, drawing, piano"
              required
              className="skillmap-input"
            />
          </div>

          <div className="skillmap-form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe this skill..."
              rows={4}
              className="skillmap-textarea"
            />
          </div>

          <div className="skillmap-form-group">
            <label htmlFor="color">Color</label>
            <div className="skillmap-color-picker">
              {colorOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`skillmap-color-swatch ${color === option.value ? 'selected' : ''}`}
                  onClick={() => setColor(option.value)}
                  title={option.label}
                  style={{
                    backgroundColor: option.color,
                    border: color === option.value ? '2px solid #333333' : '1px solid #e0e0e0'
                  }}
                >
                  {color === option.value && (
                    <span className="skillmap-color-check">✓</span>
                  )}
                </button>
              ))}
            </div>
            <small className="skillmap-form-help">
              Choose a color for this skill row in the roadmap. Selected: {colorOptions.find(opt => opt.value === color)?.label || 'Default (White)'}
            </small>
          </div>

          {isEditing && (
            <div className="skillmap-form-group">
              <label>Linked Goals</label>
              <div className="skillmap-linked-goals">
                {linkedGoals.length === 0 ? (
                  <p className="skillmap-no-goals">No goals linked</p>
                ) : (
                  linkedGoals.map((goal) => (
                    <div key={goal.id} className="skillmap-linked-goal-item">
                      <div className="skillmap-goal-info">
                        <button
                          type="button"
                          onClick={() => handleGoalClick(goal.id)}
                          className="skillmap-goal-title-link"
                        >
                          {goal.title}
                        </button>
                        {(goal.startedDate || goal.targetDate) && (
                          <div className="skillmap-goal-dates">
                            {goal.startedDate && formatDate(goal.startedDate) && (
                              <span className="skillmap-goal-date">
                                Start: {formatDate(goal.startedDate)}
                              </span>
                            )}
                            {goal.targetDate && formatDate(goal.targetDate) && (
                              <span className="skillmap-goal-date">
                                End: {formatDate(goal.targetDate)}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => handleUnlinkGoal(parseInt(goal.id))}
                        className="skillmap-unlink-btn"
                      >
                        Remove
                      </button>
                    </div>
                  ))
                )}
                <div className="skillmap-add-goal-section">
                  {!showGoalSelector && !showCreateGoalForm ? (
                    <div className="skillmap-add-goal-buttons">
                      <button
                        type="button"
                        onClick={() => setShowGoalSelector(true)}
                        className="skillmap-add-goal-btn"
                      >
                        + Link Goal
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowCreateGoalForm(true)}
                        className="skillmap-add-goal-btn"
                      >
                        + Create New Goal
                      </button>
                    </div>
                  ) : showCreateGoalForm ? (
                    <div className="skillmap-create-goal-form">
                      <h4 className="skillmap-create-goal-title">Create New Goal</h4>
                      <div className="skillmap-form-group">
                        <label htmlFor="newGoalTitle">Title *</label>
                        <input
                          type="text"
                          id="newGoalTitle"
                          value={newGoalTitle}
                          onChange={(e) => setNewGoalTitle(e.target.value)}
                          placeholder="Enter goal title..."
                          className="skillmap-input"
                        />
                      </div>
                      <div className="skillmap-form-group">
                        <label htmlFor="newGoalDescription">Description</label>
                        <textarea
                          id="newGoalDescription"
                          value={newGoalDescription}
                          onChange={(e) => setNewGoalDescription(e.target.value)}
                          placeholder="Describe your goal..."
                          rows={3}
                          className="skillmap-textarea"
                        />
                      </div>
                      <div className="skillmap-form-group">
                        <label htmlFor="newGoalCategory">Category</label>
                        <select
                          id="newGoalCategory"
                          value={newGoalCategory}
                          onChange={(e) => setNewGoalCategory(e.target.value as GoalCategory)}
                          className="skillmap-select"
                        >
                          {Object.values(GoalCategory).map(cat => (
                            <option key={cat} value={cat}>
                              {cat.replace('_', ' ').toUpperCase()}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="skillmap-form-group">
                        <label htmlFor="newGoalStartedDate">Started Date</label>
                        <input
                          type="date"
                          id="newGoalStartedDate"
                          value={newGoalStartedDate}
                          onChange={(e) => setNewGoalStartedDate(e.target.value)}
                          className="skillmap-input"
                        />
                      </div>
                      <div className="skillmap-form-group">
                        <label htmlFor="newGoalTargetDate">Target Date</label>
                        <input
                          type="date"
                          id="newGoalTargetDate"
                          value={newGoalTargetDate}
                          onChange={(e) => setNewGoalTargetDate(e.target.value)}
                          className="skillmap-input"
                        />
                      </div>
                      <div className="skillmap-goal-selector-actions">
                        <button
                          type="button"
                          onClick={handleCreateGoal}
                          disabled={creatingGoal || !newGoalTitle.trim()}
                          className="skillmap-link-goal-btn"
                        >
                          {creatingGoal ? 'Creating...' : 'Create & Link'}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowCreateGoalForm(false);
                            setNewGoalTitle('');
                            setNewGoalDescription('');
                            setNewGoalCategory(GoalCategory.PERSONAL);
                            setNewGoalStartedDate('');
                            setNewGoalTargetDate('');
                          }}
                          disabled={creatingGoal}
                          className="skillmap-cancel-link-btn"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="skillmap-goal-selector">
                      <select
                        value={selectedGoalId}
                        onChange={(e) => setSelectedGoalId(e.target.value)}
                        className="skillmap-select"
                      >
                        <option value="">Select a goal...</option>
                        {getUnlinkedGoals().map((goal) => (
                          <option key={goal.id} value={goal.id}>
                            {goal.title}
                          </option>
                        ))}
                      </select>
                      <div className="skillmap-goal-selector-actions">
                        <button
                          type="button"
                          onClick={handleLinkGoal}
                          disabled={!selectedGoalId}
                          className="skillmap-link-goal-btn"
                        >
                          Link
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowGoalSelector(false);
                            setSelectedGoalId('');
                          }}
                          className="skillmap-cancel-link-btn"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="skillmap-form-actions">
            <button
              type="button"
              onClick={() => navigate('/skillmap')}
              className="skillmap-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || deleting || !name.trim()}
              className="skillmap-save-btn"
            >
              {saving ? 'Saving...' : (isEditing ? 'Update Skill' : 'Create Skill')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SkillEdit;

